import { IF, nil, NO, UIColor, UITextView, UITimer, UIView, YES } from "uicore-ts"
import { UITooltip } from "uicore-ts/compiledScripts/UITooltip"
// @ts-ignore
import { RHProcurementOffer, RHProcurementPartOffer } from "../../../../models/OfferModels"
import { RHProcurement, RHProcurementPart } from "../../../../models/ProcurementModels"
import { RHCore } from "../../Core/RHCore"
import { RHFeatureFlags } from "../../Core/RHFeatureFlags"
import { CBColumnDescriptor, CBDataViewCellDescriptorWithObject } from "../../Custom components/CB/CBDataView"
import { CBDialogViewShower } from "../../Custom components/CB/CBDialogViewShower"
import { CBTableRowView } from "../../Custom components/CB/CBTableRowView"
import { CellView } from "../../Custom components/CellView"
import { RHFocusManager } from "../../Custom components/RH/Keyboard navigation/RHFocusManager"
import { RHAlertBannerView } from "../../Custom components/RH/RHAlertBannerView"
import { RHButton } from "../../Custom components/RH/RHButton"
import { RHCheckbox } from "../../Custom components/RH/RHCheckbox"
import { RHConflictOfInterestReasonTranslations } from "../../Custom components/RH/RHConflictOfInterestBannerView"
import { RHColor } from "../../Custom components/RH/RHColor"
import { RHDataView } from "../../Custom components/RH/RHDataView"
import { RHFileListView } from "../../Custom components/RH/RHFileListView"
import { RHPanelView } from "../../Custom components/RH/RHPanelView"
import { RHTableActionButtonView } from "../../Custom components/RH/RHTableActionButtonView"
import { formatRHDateTime, formatRHDateTimeValue } from "../../Custom components/RH/Utils_slop/RHDateFormatting"
import { RHButtons, RHColors, RHFont, RHSpacing, RHTables } from "../../Custom components/RH/Utils_slop/RHDesignTokens"
import { RHEuroFormatter } from "../../Custom components/RH/Utils_slop/RHEuroFormatter"
import { RHProcurementDeadlineResolutionTrigger } from "../../Custom components/RH/Utils_slop/RHProcurementDeadlineResolutionTrigger"
import { RHProcurementPartDisplay } from "../../Custom components/RH/Utils_slop/RHProcurementPartDisplay"
import { RHTextSanitizer } from "../../Custom components/RH/Utils_slop/RHTextSanitizer"
import { RHOffersEvaluationViewTokens, RHTableColumnMinimumWidths } from "../../Custom components/RH/Utils_slop/RHViewTokens"
import { RowView } from "../../Custom components/RowView"
import type {
    RHConflictOfInterestReasonCode,
    RHIncomingOfferEnvelope,
    RHOfferNotificationSnapshot,
    RHProcurementCapabilities,
    RHProcurementTendererOverview
} from "../../SocketClientFunctions"
import { RHProcurementPartView } from "../RHProcurementPartView"
import { EvaluationNotesDialogView } from "./EvaluationNotesDialogView"
import { PartDecisionChangeDialogView } from "./PartDecisionChangeDialogView"
import { PartOfferDocumentsHeaderView } from "./PartOfferDocumentsHeaderView"
import { populatedValueOfReference } from "../../Core/RHReference"
import { referenceId } from "../../Core/RHReference"


// ─────────────────────────────────────────────────────────────────────────────
// Pre-opening view: registered tenderers and submitted-offer status, no prices
// ─────────────────────────────────────────────────────────────────────────────

interface CompanyRow {
    companyId: string
    companyName: string
    isRegistered: boolean
    registeredAt?: Date
    hasSubmittedOffer: boolean
    submittedAt?: Date
    conflictOfInterestReasons: RHConflictOfInterestReasonCode[]
}


type EvaluationPartOffer = RHProcurementPartOffer & {
    offererCompanyName: string
    offerSubmissionDetailsText: string
    conflictOfInterestReasons?: RHConflictOfInterestReasonCode[]
}


// ─────────────────────────────────────────────────────────────────────────────
// Main view
// ─────────────────────────────────────────────────────────────────────────────

export class RHOffersEvaluationView extends UIView {
    
    
    // Title
    readonly titleLabel = new UITextView(undefined, UITextView.type.header4)
        .configuredWithObject({
            text: "Pakkumused",
            textColor: RHColors.textPrimary,
            backgroundColor: UIColor.transparentColor
        })
        .addedAsSubviewToView(this)
    
    // Pre-opening: one row per registered tenderer or submitted-offer company.
    readonly offerersDataView = new RHDataView<CompanyRow>()
        .configuredWithObject({
            tableView: {
                allRowsHaveEqualHeight: NO
            },
            isSearchAvailable: NO,
            maximumIntrinsicContentHeight: 500
        })
        .addedAsSubviewToView(this)
    
    // Post-deadline evaluation: one section per part
    _partSections: RHPartOfferEvaluationSection[] = []
    
    // Empty state
    readonly emptyStateLabel = new UITextView()
        .configuredWithObject({
            text: "Pakkumusi pole veel esitatud",
            textColor: RHColor.textSecondary,
            backgroundColor: UIColor.transparentColor,
            style: {
                // fontSize: "16px",
                fontSize: "16px",
                textAlign: "center",
                // padding: "40px 20px"
                padding: "20px 20px 48px"
            }
        })
    
    readonly openingRequiredLabel = new RHAlertBannerView()
        .configuredWithObject({
            text: "Pakkumuste sisu on peidetud. Pakkumuste hindamiseks ava pakkumused ja teavita pakkujaid. Kui sa ei soovi pakkumusi veel avada, saad hanke andmeid muuta ja pakkumuste esitamise tähtaega pikendada.",
            alertType: RHAlertBannerView.type.Warning,
            textPresentation: RHAlertBannerView.textPresentation.Outcome
        })
    
    readonly closedWithoutContractInfoLabel = new RHAlertBannerView({
        text: RHProcurementPartDisplay.closedWithoutContractStatusText(1),
        alertType: RHAlertBannerView.type.Warning,
        textPresentation: RHAlertBannerView.textPresentation.Outcome
    })

    readonly openOffersButton = new RHButton()
        .configuredWithObject({
            titleLabel: { text: RHOffersEvaluationView._openOffersButtonDefaultTitle },
            variant: RHOffersEvaluationViewTokens.finalizeButtonVariant,
            contentPadding: RHButtons.compactContentPadding,
            style: {
                fontSize: RHButtons.compactFontSize
            },
            controlEventTargetAccumulator: {
                PrimaryActionTriggered: () => {
                    this._confirmOpenOffersAndNotifyOfferers()
                }
            }
        })
    
    displayedPartId?: string
    numberOfRootProcurementParts?: number
    isPartClosedWithoutContractInRoundHistory = NO
    isReadOnly = NO
    hasNextRound = NO
    additionalActionButton?: RHButton

    // State
    _procurement?: RHProcurement
    _procurementCapabilities?: RHProcurementCapabilities
    _offers: (RHProcurementOffer | RHIncomingOfferEnvelope)[] = nil
    _tendererOverview?: RHProcurementTendererOverview[]
    _isOpeningOffers = NO
    offersWereOpened?: () => void
    partEvaluationDidFinalize?: () => void
    createReconciliationDidRequest?: (procurementId: string, partId: string) => void
    cancelPartDidRequest?: (procurementId: string, partId: string) => void
    evaluationContentDidLoad?: (
        procurementId: string,
        snapshot: RHOfferNotificationSnapshot
    ) => void
    _loadedNotificationSnapshot?: RHOfferNotificationSnapshot
    _contentLoadSequence = 0
    readonly _deadlineResolutionTrigger = new RHProcurementDeadlineResolutionTrigger()
    _procurementDeadlineTimer?: UITimer
    _lastDeliveredNotificationSnapshotKey?: string
    
    override _focusManager = new RHFocusManager(() => {
        const items: UIView[] = []
        if (this.offerersDataView.superview === this) {
            items.push(this.offerersDataView.searchTextField)
            items.push(this.offerersDataView._gridContainerView)
        }
        if (this.openOffersButton.superview === this) {
            items.push(this.openOffersButton)
        }
        if (this.additionalActionButton?.superview === this) {
            items.push(this.additionalActionButton)
        }
        for (const section of this._partSections) {
            if (section._focusManager) {
                items.push(...(section._focusManager.focusableItems() as UIView[]))
            }
        }
        return items
    })
    
    constructor(elementID?: string) {
        super(elementID);
        (this as RHOffersEvaluationView).configureWithObject({
            backgroundColor: UIColor.transparentColor
        })
        this.offerersDataView.configureWithObject({
            backgroundColor: UIColor.whiteColor,
            style: {
                border: "none",
                boxShadow: "none"
            }
        })
        
        this._applyOfferersTableColumnLayout()
    }
    
    
    _offerersTableDescriptors(): CBColumnDescriptor<CompanyRow>[] {
        return [
            CBDataViewCellDescriptorWithObject({
                title: "Pakkuja",
                keyPath: "companyName",
                defaultTitle: "Tundmatu ettevõte",
                allowSorting: YES,
                titleLabelAlignment: UITextView.textAlignment.left,
                cellLabelAlignment: UITextView.textAlignment.left,
                updateCell: (cellView, dataItem: CompanyRow) => {
                    let text = dataItem.companyName
                    if (dataItem.conflictOfInterestReasons.length > 0) {
                        text += "\n⚠ Võimalik huvide konflikt"
                    }
                    cellView.titleLabel!.configureWithObject({
                        text,
                        isSingleLine: NO,
                        style: { whiteSpace: "pre-line" }
                    })
                    cellView.hoverText = dataItem.conflictOfInterestReasons
                        .map(reason => RHConflictOfInterestReasonTranslations[reason])
                        .join("\n")
                }
            }),
            CBDataViewCellDescriptorWithObject({
                title: "Registreerunud",
                keyPath: "isRegistered",
                allowSorting: YES,
                dataType: "string",
                titleLabelAlignment: UITextView.textAlignment.center,
                cellLabelAlignment: UITextView.textAlignment.center,
                updateCell: (cellView, dataItem: CompanyRow) => {
                    cellView.titleLabel!.text = this._yesNoText(dataItem.isRegistered)
                }
            }),
            CBDataViewCellDescriptorWithObject({
                title: "Registreerimise aeg",
                keyPath: "registeredAt",
                allowSorting: YES,
                dataType: "string",
                titleLabelAlignment: UITextView.textAlignment.left,
                cellLabelAlignment: UITextView.textAlignment.left,
                updateCell: (cellView, dataItem: CompanyRow) => {
                    cellView.titleLabel!.text = this._formattedOverviewDate(dataItem.registeredAt)
                }
            }),
            CBDataViewCellDescriptorWithObject({
                title: "Pakkumuse esitanud",
                keyPath: "hasSubmittedOffer",
                allowSorting: YES,
                dataType: "string",
                titleLabelAlignment: UITextView.textAlignment.center,
                cellLabelAlignment: UITextView.textAlignment.center,
                updateCell: (cellView, dataItem: CompanyRow) => {
                    cellView.titleLabel!.text = this._yesNoText(dataItem.hasSubmittedOffer)
                }
            }),
            CBDataViewCellDescriptorWithObject({
                title: "Pakkumuse esitamise aeg",
                keyPath: "submittedAt",
                allowSorting: YES,
                dataType: "string",
                titleLabelAlignment: UITextView.textAlignment.left,
                cellLabelAlignment: UITextView.textAlignment.left,
                updateCell: (cellView, dataItem: CompanyRow) => {
                    cellView.titleLabel!.text = this._formattedOverviewDate(dataItem.submittedAt)
                }
            })
        ]
    }


    _yesNoText(value: boolean): string {
        if (value) {
            return "Jah"
        }
        return "Ei"
    }


    _formattedOverviewDate(value?: Date): string {
        if (!value) {
            return "-"
        }
        return formatRHDateTime(value)
    }
    
    
    _applyOfferersTableColumnLayout() {
        const columnWeights = RHOffersEvaluationViewTokens.tendererOverviewColumnWeights
        this.offerersDataView.descriptors = this._offerersTableDescriptors()
        this.offerersDataView.tableHeaderView.configureWithObject({
            cellWeights: columnWeights,
            intrinsicContentHeight: () => 64
        })
        this.offerersDataView.tableView.configureWithObject({
            cellWeights: columnWeights
        })
        this._configureOfferersTableHeaderCells()
        this.offerersDataView.tableHeaderView.setNeedsLayout()
        this.offerersDataView.tableView.setNeedsLayout()
    }
    
    
    _configureOfferersTableHeaderCells() {
        for (const headerCell of this.offerersDataView.tableHeaderView.cells) {
            const buttonView = headerCell.buttonView
            if (buttonView) {
                buttonView.contentPadding = 2
            }
            const titleLabel = buttonView?.titleLabel ?? headerCell.titleLabel
            if (!titleLabel) {
                continue
            }
            titleLabel.isSingleLine = NO
            titleLabel.style.whiteSpace = "normal"
            titleLabel.style.lineHeight = RHOffersEvaluationViewTokens.offerersMultilineHeaderLineHeight
            titleLabel.textElementView.style.whiteSpace = "normal"
            titleLabel.textElementView.style.lineHeight = RHOffersEvaluationViewTokens.offerersMultilineHeaderLineHeight
        }
    }
    
    set procurement(procurement: RHProcurement) {
        void this.loadForProcurement(procurement)
    }

    /**
     * Starts a fresh incoming-offers load for one procurement and resolves
     * only after that content has been applied to the view. Callers that need
     * an explicit load barrier may await this method; the property setter is
     * retained for existing fire-and-forget refreshes.
     */
    async loadForProcurement(procurement: RHProcurement): Promise<boolean> {
        this._procurement = procurement
        this._deadlineResolutionTrigger.reset()
        this._scheduleProcurementDeadlineResolutionRequest()
        // Clear stale offers so updateContentForCurrentProcurement always fetches fresh.
        this._offers = nil
        this._tendererOverview = undefined
        this._loadedNotificationSnapshot = undefined
        this._lastDeliveredNotificationSnapshotKey = undefined
        this._applyOfferersTableColumnLayout()
        return this.updateContentForCurrentProcurement()
    }
    
    get procurement(): RHProcurement | undefined {
        return this._procurement
    }

    /** Exact server-bounded offer content currently represented by this view. */
    get loadedNotificationSnapshot(): RHOfferNotificationSnapshot | undefined {
        if (!this._loadedNotificationSnapshot) {
            return undefined
        }
        return {
            offerIds: [...this._loadedNotificationSnapshot.offerIds],
            auditCutoff: this._loadedNotificationSnapshot.auditCutoff
        }
    }
    
    set procurementCapabilities(capabilities: RHProcurementCapabilities | undefined) {
        this._procurementCapabilities = capabilities
        for (const section of this._partSections) {
            section.procurementCapabilities = capabilities
        }
        this.setNeedsLayoutUpToRootView()
    }
    
    get procurementCapabilities(): RHProcurementCapabilities | undefined {
        return this._procurementCapabilities
    }
    
    get _canOpenOffers(): boolean {
        if (!this._procurementCapabilities || this.isReadOnly) {
            return NO
        }
        return this._procurementCapabilities.canEvaluate === YES
    }
    
    // Deadline pushes must update opening controls without rebuilding ongoing evaluation work.
    refreshProcurementMetadata(procurement: RHProcurement) {
        this._procurement = procurement
        this._scheduleProcurementDeadlineResolutionRequest()
        if (this._partSections.length === 0) {
            void this.updateContentForCurrentProcurement()
        }
        for (const section of this._partSections) {
            section._procurement = procurement
            section._isEvaluationFinalizable = this.isEvaluationFinalizable
            section.procurementCapabilities = this._procurementCapabilities
        }
        this.setNeedsLayoutUpToRootView()
    }

    _scheduleProcurementDeadlineResolutionRequest() {
        this._procurementDeadlineTimer?.invalidate()
        this._procurementDeadlineTimer = undefined
        const procurementId = this.procurement?._id?.toString()
        const deadlineDate = this.procurement?.deadlineDate
        if (!this.isMemberOfViewTree || this.isReadOnly || !procurementId || !deadlineDate ||
            this.procurement?.status !== "published" || this._offersHaveBeenOpened) {
            return
        }
        const deadlineTimestamp = new Date(deadlineDate).getTime()
        if (!Number.isFinite(deadlineTimestamp)) {
            return
        }
        const millisecondsUntilDeadline = deadlineTimestamp - Date.now() + 1
        if (millisecondsUntilDeadline > 0) {
            this._deadlineResolutionTrigger.reset()
            // Browser timers overflow beyond 2^31 - 1 ms; recheck long deadlines in bounded steps.
            this._procurementDeadlineTimer = new UITimer(
                Math.min(millisecondsUntilDeadline, 2_147_483_647) / 1000,
                NO,
                () => this._scheduleProcurementDeadlineResolutionRequest()
            )
            return
        }
        this._deadlineResolutionTrigger.requestResolutionForExpiredDeadlines([{ procurementId, deadlineDate }])
    }
    
    override wasAddedToViewTree() {
        super.wasAddedToViewTree()
        RHCore.sharedInstance.offerStore.subscribe(this, (changedProcurementIds) => {
            this._handleIncomingOffersDidChange(changedProcurementIds)
        })
        this._scheduleProcurementDeadlineResolutionRequest()
        if (this._offers !== nil && this._partSections.length === 0) {
            void this.updateContentForCurrentProcurement()
        }
        // Content may have finished loading while this lazy tab was detached.
        // Deliver its snapshot only now that the user can actually see the view.
        this._deliverLoadedNotificationSnapshot()
    }
    
    override wasRemovedFromViewTree() {
        super.wasRemovedFromViewTree()
        this._procurementDeadlineTimer?.invalidate()
        this._procurementDeadlineTimer = undefined
        RHCore.sharedInstance.offerStore.unsubscribe(this)
    }
    
    /**
     * Reacts to RHOfferStore notifying us that incoming offers changed
     * somewhere. Two refinements over a blanket reload:
     *
     * 1. If the push tells us which procurements were affected and ours is
     *    not among them, the change is entirely irrelevant to this view
     *    instance — do nothing.
     * 2. If we are already showing the post-deadline evaluation sections,
     *    refresh their data in place instead of tearing the whole view down
     *    and rebuilding it — this is what previously caused every small
     *    evaluation action (including this view's own writes, which are
     *    echoed back as a push to the whole procuring company) to trigger a
     *    disruptive full-view refresh.
     */
    _handleIncomingOffersDidChange(changedProcurementIds?: string[]) {
        const currentProcurementId = this.procurement?._id?.toString()
        if (!currentProcurementId) {
            return
        }
        
        if (changedProcurementIds && !changedProcurementIds.includes(currentProcurementId)) {
            return
        }
        
        if (this._partSections.length > 0) {
            void this._refreshPartOffersInPlace()
            return
        }
        
        this._offers = nil
        void this.updateContentForCurrentProcurement()
    }
    
    get isEvaluationFinalizable() {
        return !this.isReadOnly && this.procurement?.status === "published" && this.hasProcurementDeadlinePassed
    }
    
    get hasProcurementDeadlinePassed() {
        return new Date(this.procurement?.deadlineDate ?? Date.now()).getTime() < Date.now()
    }
    
    get isEvaluationFinalized() {
        const status = this.procurement?.status
        return status === "evaluation-finalized" ||
            status === "closed-no-winner" ||
            status === "closed-without-contract"
    }
    
    async updateContentForCurrentProcurement(): Promise<boolean> {
        const procurementId = this.procurement?._id?.toString()
        if (!procurementId) {
            return NO
        }
        const loadSequence = ++this._contentLoadSequence
        
        try {
            const contentSnapshot = await RHCore.sharedInstance.offerStore
                .retrieveIncomingOffersSnapshotForProcurement(
                    procurementId
                )
            if (
                loadSequence !== this._contentLoadSequence ||
                this.procurement?._id?.toString() !== procurementId
            ) {
                return NO
            }
            this._offers = contentSnapshot.offers
            this._tendererOverview = contentSnapshot.tenderers
            const companyRows = this._buildCompanyRows()
            const loadedNotificationSnapshot = this._notificationSnapshotForContent(
                contentSnapshot.offers,
                contentSnapshot.notificationSnapshotAt
            )
            // Prefer the snapshot's own verdict: the cached procurement's offersOpenedAt can still be mid-flight from a concurrent open action.
            const areOffersOpenForFetchedContent = contentSnapshot.areOffersOpen ?? this._offersHaveBeenOpened
            
            if (
                this._offers.length > 0 &&
                this.hasProcurementDeadlinePassed &&
                areOffersOpenForFetchedContent
            ) {
                this.emptyStateLabel.removeFromSuperview()
                // Evaluation mode: grouped by part.
                this.offerersDataView.removeFromSuperview()
                this.openingRequiredLabel.removeFromSuperview()
                this.openOffersButton.removeFromSuperview()
                this._buildPartSections()
            }
            else if (companyRows.length > 0) {
                this.emptyStateLabel.removeFromSuperview()
                this._clearPartSections()
                this.offerersDataView.addedAsSubviewToView(this)
                this.offerersDataView.data = companyRows

                if (
                    this.procurement?.status === "published" &&
                    !this.isPartClosedWithoutContractInRoundHistory &&
                    this.hasProcurementDeadlinePassed &&
                    this._offers.length > 0
                ) {
                    this.openingRequiredLabel.addedAsSubviewToView(this)
                    if (this._canOpenOffers) {
                        this.openOffersButton.addedAsSubviewToView(this)
                        this._updateOpenOffersButtonInteractionState()
                    }
                    else {
                        this.openOffersButton.removeFromSuperview()
                    }
                }
                else {
                    this.openingRequiredLabel.removeFromSuperview()
                    this.openOffersButton.removeFromSuperview()
                }
            }
            else {
                this._clearPartSections()
                this.offerersDataView.removeFromSuperview()
                this.openingRequiredLabel.removeFromSuperview()
                this.openOffersButton.removeFromSuperview()
                this.emptyStateLabel.textColor = RHColor.textSecondary
                this.emptyStateLabel.text = IF(this.isPartClosedWithoutContractInRoundHistory)(() =>
                    RHProcurementPartDisplay.noPartOffersText(this.numberOfRootProcurementParts)
                ).ELSE_IF(this.isEvaluationFinalized)(() =>
                    "Hange on lõpetatud. Pakkumusi ei esitatud."
                ).ELSE_IF(this.procurement?.status === "closed-without-contract")(() =>
                    "Ühtegi pakkujat ei registreerunud ega pakkumust esitatud."
                ).ELSE_IF(this.hasProcurementDeadlinePassed)(() =>
                    "Ühtegi pakkujat ei registreerunud ega pakkumust esitatud. Saad hanke andmeid muuta ja pakkumuste esitamise tähtaega pikendada."
                ).ELSE(() =>
                    "Pakkujaid pole veel registreerunud ega pakkumusi esitatud"
                )
                this.emptyStateLabel.addedAsSubviewToView(this)
            }
            
            this._loadedNotificationSnapshot = loadedNotificationSnapshot
            this.setNeedsLayoutUpToRootView()
            this._deliverLoadedNotificationSnapshot()
            return YES
        }
        catch (error) {
            if (
                loadSequence !== this._contentLoadSequence ||
                this.procurement?._id?.toString() !== procurementId
            ) {
                return NO
            }
            console.error("Error loading offers:", error)
            this._loadedNotificationSnapshot = undefined
            this._clearPartSections()
            this.offerersDataView.removeFromSuperview()
            this.openingRequiredLabel.removeFromSuperview()
            this.openOffersButton.removeFromSuperview()
            this.emptyStateLabel.text = "Viga pakkumuste laadimisel"
            this.emptyStateLabel.textColor = RHColor.danger
            this.emptyStateLabel.addedAsSubviewToView(this)
            return NO
        }
    }

    _notificationSnapshotForContent(
        offers: (RHProcurementOffer | RHIncomingOfferEnvelope)[],
        notificationSnapshotAt: string
    ): RHOfferNotificationSnapshot | undefined {
        if (
            !notificationSnapshotAt ||
            Number.isNaN(new Date(notificationSnapshotAt).getTime())
        ) {
            // Keep the loaded content visible, but do not acknowledge anything
            // without a trustworthy server-issued cutoff.
            return undefined
        }
        const offerIds = [...new Set(
            offers
                .map(offer => offer._id?.toString())
                .filter((offerId): offerId is string => !!offerId)
        )]
        return {
            offerIds,
            auditCutoff: notificationSnapshotAt
        }
    }

    _deliverLoadedNotificationSnapshot() {
        const procurementId = this.procurement?._id?.toString()
        const snapshot = this._loadedNotificationSnapshot
        if (!this.superview || !procurementId || !snapshot) {
            return
        }
        const snapshotKey = [
            procurementId,
            snapshot.auditCutoff,
            ...snapshot.offerIds
        ].join("|")
        if (snapshotKey === this._lastDeliveredNotificationSnapshotKey) {
            return
        }
        this._lastDeliveredNotificationSnapshotKey = snapshotKey
        this.evaluationContentDidLoad?.(procurementId, {
            offerIds: [...snapshot.offerIds],
            auditCutoff: snapshot.auditCutoff
        })
    }
    
    // ── Private helpers ───────────────────────────────────────────────────────
    
    get _offersHaveBeenOpened(): boolean {
        const offersOpenedAt = this.procurement?.offersOpenedAt
        if (!offersOpenedAt) {
            return NO
        }
        return new Date(offersOpenedAt).getTime() <= Date.now()
    }
    
    
    _confirmOpenOffersAndNotifyOfferers() {
        if (!this.procurement?._id) {
            return
        }
        
        const shower = CBDialogViewShower.showQuestionDialog(
            RHOffersEvaluationView._openOffersConfirmationText
        )
        shower.yesButtonWasPressed = () => {
            shower.dialogView.dismiss()
            void this._openOffersAndNotifyOfferers()
        }
    }
    
    
    async _openOffersAndNotifyOfferers() {
        if (!this.procurement?._id) {
            return
        }
        
        this._isOpeningOffers = YES
        this._updateOpenOffersButtonInteractionState()
        
        try {
            const response = await RHCore.sharedInstance.offerStore.openProcurementOffers(
                this.procurement._id.toString()
            )
            
            const procurement = this._procurement
            if (procurement) {
                procurement.offersOpenedAt = response.openedAt
                procurement.offersOpenedBy = response.procurement.offersOpenedBy
            }
            this.offersWereOpened?.()
            
            this._offers = nil
            await this.updateContentForCurrentProcurement()
        }
        catch (error) {
            console.error("Error opening offers:", error)
            CBDialogViewShower.alert("Pakkumuste avamine ebaõnnestus. Palun proovi uuesti.")
        }
        finally {
            // Concurrent pushes/subscriptions can re-render this button mid-request
            // (see updateContentForCurrentProcurement); resync it here too so it never
            // gets stuck showing the busy title if one of those wins the render race.
            this._isOpeningOffers = NO
            this._updateOpenOffersButtonInteractionState()
        }
    }
    
    
    _updateOpenOffersButtonInteractionState() {
        this.openOffersButton.enabled = !this._isOpeningOffers
        this.openOffersButton.titleLabel.text = IF(this._isOpeningOffers)(() =>
            RHOffersEvaluationView._openOffersButtonBusyTitle
        ).ELSE(() =>
            RHOffersEvaluationView._openOffersButtonDefaultTitle
        )
    }
    
    _buildCompanyRows(): CompanyRow[] {
        if (this._tendererOverview !== undefined) {
            return this._tendererOverview.map(tenderer => ({
                companyId: tenderer.companyId,
                companyName: tenderer.companyName,
                isRegistered: tenderer.isRegistered,
                registeredAt: this._overviewDate(tenderer.registeredAt),
                hasSubmittedOffer: tenderer.hasSubmittedOffer,
                submittedAt: this._overviewDate(tenderer.submittedAt),
                conflictOfInterestReasons: tenderer.conflictOfInterestReasons ?? []
            }))
        }

        // Rolling-deployment fallback for an older server: combine the legacy
        // registeredTenderers list with the offer envelopes already returned.
        // Exact registration timestamps become available as soon as the new
        // server snapshot is active.
        const rowsByCompanyId = new Map<string, CompanyRow>()
        const procuringCompanyId = referenceId(this.procurement?.procuringCompany)
        for (const companyReference of this.procurement?.registeredTenderers ?? []) {
            const companyId = referenceId(companyReference)
            if (!companyId) {
                continue
            }
            if (companyId === procuringCompanyId) {
                continue
            }
            const companyName = populatedValueOfReference(companyReference)?.name ?? "Tundmatu ettevõte"
            rowsByCompanyId.set(companyId, {
                companyId,
                companyName,
                isRegistered: YES,
                hasSubmittedOffer: NO,
                conflictOfInterestReasons: []
            })
        }

        for (const offer of this._offers) {
            const companyReference = offer.offererCompany
            const companyId = referenceId(companyReference)
            if (!companyId) {
                continue
            }
            if (companyId === procuringCompanyId) {
                continue
            }
            const companyName = populatedValueOfReference(companyReference)?.name ?? "Tundmatu ettevõte"
            const existingRow = rowsByCompanyId.get(companyId)
            if (existingRow) {
                existingRow.companyName = companyName
                existingRow.hasSubmittedOffer = YES
                existingRow.conflictOfInterestReasons = [
                    ...new Set([
                        ...existingRow.conflictOfInterestReasons,
                        ...(offer.conflictOfInterestReasons ?? [])
                    ])
                ]
                if (!existingRow.submittedAt) {
                    existingRow.submittedAt = this._overviewDate(offer.submittedAt)
                }
                continue
            }
            rowsByCompanyId.set(companyId, {
                companyId,
                companyName,
                isRegistered: NO,
                hasSubmittedOffer: YES,
                submittedAt: this._overviewDate(offer.submittedAt),
                conflictOfInterestReasons: offer.conflictOfInterestReasons ?? []
            })
        }
        return Array.from(rowsByCompanyId.values())
            .sort((firstRow, secondRow) => firstRow.companyName.localeCompare(secondRow.companyName, "et"))
    }


    _overviewDate(value?: string | Date): Date | undefined {
        if (!value) {
            return undefined
        }
        const date = new Date(value)
        if (Number.isNaN(date.getTime())) {
            return undefined
        }
        return date
    }
    
    _buildPartSections() {
        this._clearPartSections()
        
        const parts = (this.procurement?.parts ?? []).filter(part =>
            !this.displayedPartId || part._id.toString() === this.displayedPartId
        )
        const partOffersByPartId = this._flattenPartOffersByPartId()
        
        // Build one section per part in part-number order
        for (const part of parts) {
            const partIdString = part._id.toString()
            const section = new RHPartOfferEvaluationSection(
                part,
                this.isEvaluationFinalizable,
                () => {
                    this.setNeedsLayout()
                },
                () => {
                    this.partEvaluationDidFinalize?.()
                },
                this._procurement,
                this._procurementCapabilities,
                this.createReconciliationDidRequest,
                this.cancelPartDidRequest,
                this.isReadOnly,
                !!this.displayedPartId && this.hasNextRound
            ).configuredWithObject({
                numberOfRootProcurementParts: this.numberOfRootProcurementParts,
                isPartClosedWithoutContractInRoundHistory: !!this.displayedPartId && this.isPartClosedWithoutContractInRoundHistory
            }).addedAsSubviewToView(this) as RHPartOfferEvaluationSection
            if (this.displayedPartId) {
                section.configureWithObject({ style: { border: "none", boxShadow: "none" } })
            }

            section.configureWithPartOffers(partOffersByPartId.get(partIdString) ?? [])
            this._partSections.push(section)
        }
    }
    
    /**
     * Flattens every active part offer across all submitted offer packages
     * into per-part groups, annotating each with the offering company's name.
     * NOTE: we mutate the part offer objects in-place (adding
     * offererCompanyName) rather than shallow-copying them, so that all
     * sections share the same object references — this matters for
     * mergeFreshPartOffers, which relies on identity-free, plain-data
     * comparisons rather than caring which array an object originally came
     * from.
     */
    _flattenPartOffersByPartId(): Map<string, EvaluationPartOffer[]> {
        const partOffersByPartId = new Map<string, EvaluationPartOffer[]>()
        
        for (const offer of this._offers) {
            const companyName = populatedValueOfReference(offer.offererCompany)?.name ?? "Tundmatu ettevõte"
            const partOffers = (offer.partOffers as RHProcurementPartOffer[]) ?? []
            for (const partOffer of partOffers) {
                const presentationPartOffer = partOffer as EvaluationPartOffer
                presentationPartOffer.offererCompanyName = companyName
                presentationPartOffer.offerSubmissionDetailsText =
                    RHOffersEvaluationView._detailsTextForOffer(offer)
                presentationPartOffer.conflictOfInterestReasons = offer.conflictOfInterestReasons ?? []
                const partIdString = partOffer.partId.toString()
                const existingGroup = partOffersByPartId.get(partIdString)
                if (existingGroup) {
                    existingGroup.push(presentationPartOffer)
                }
                else {
                    partOffersByPartId.set(partIdString, [presentationPartOffer])
                }
            }
        }
        
        return partOffersByPartId
    }
    
    /**
     * Re-fetches offers for the current procurement and merges the result
     * into the already-built part sections in place, instead of tearing
     * down and recreating them. Used by _handleIncomingOffersDidChange so a
     * push notification — including the echo of this view's own writes —
     * never causes a visible full-view rebuild while evaluation is open.
     */
    async _refreshPartOffersInPlace() {
        const procurementId = this.procurement?._id?.toString()
        if (!procurementId) {
            return
        }
        const loadSequence = ++this._contentLoadSequence
        
        try {
            const contentSnapshot = await RHCore.sharedInstance.offerStore
                .retrieveIncomingOffersSnapshotForProcurement(
                    procurementId
                )
            if (
                loadSequence !== this._contentLoadSequence ||
                this.procurement?._id?.toString() !== procurementId
            ) {
                return
            }
            this._offers = contentSnapshot.offers
            this._tendererOverview = contentSnapshot.tenderers
            const loadedNotificationSnapshot = this._notificationSnapshotForContent(
                contentSnapshot.offers,
                contentSnapshot.notificationSnapshotAt
            )
            const partOffersByPartId = this._flattenPartOffersByPartId()
            
            for (const section of this._partSections) {
                const partIdString = section._part._id.toString()
                section.mergeFreshPartOffers(partOffersByPartId.get(partIdString) ?? [])
            }
            this._loadedNotificationSnapshot = loadedNotificationSnapshot
            this._deliverLoadedNotificationSnapshot()
        }
        catch (error) {
            console.error("Error refreshing offers after a push notification:", error)
            // Keep showing the last known-good state rather than disrupting
            // the view — re-entering the view will recover from a transient
            // failure here.
        }
    }
    
    _clearPartSections() {
        this._partSections.forEach(s => s.removeFromSuperview())
        this._partSections = []
    }
    
    // ── Layout ────────────────────────────────────────────────────────────────
    
    override layoutSubviews() {
        super.layoutSubviews()
        
        const padding = this.core.paddingLength
        const bounds = this.contentBoundsWithInset(0)

        if ((this.procurement?.status === "closed-without-contract" ||
            (!!this.displayedPartId && this.isPartClosedWithoutContractInRoundHistory)) && this._partSections.length === 0) {
            this.closedWithoutContractInfoLabel.configureBannerWithObject({
                text: RHProcurementPartDisplay.closedWithoutContractStatusText(this.numberOfRootProcurementParts ?? 1)
            })
            this.closedWithoutContractInfoLabel.addedAsSubviewToView(this)
        }
        else {
            this.closedWithoutContractInfoLabel.removeFromSuperview()
        }

        for (const section of this._partSections) {
            section.configureWithObject({
                hasNextRound: !!this.displayedPartId && this.hasNextRound,
                numberOfRootProcurementParts: this.numberOfRootProcurementParts,
                isPartClosedWithoutContractInRoundHistory: !!this.displayedPartId && this.isPartClosedWithoutContractInRoundHistory,
                additionalActionButton: this.additionalActionButton
            })
        }
        if (this.additionalActionButton && this._partSections.length === 0) {
            if (this.additionalActionButton.superview !== this) {
                this.additionalActionButton.removeFromSuperview()
            }
            this.additionalActionButton.addedAsSubviewToView(this)
        }
        const actionButtons = [this.openOffersButton, this.additionalActionButton]
            .filter((button): button is RHButton => !!button && button.superview === this)
        const actionButtonWidths = actionButtons.map(button =>
            button.titleLabel.intrinsicContentWidth(RHButtons.compactHeight) +
            RHButtons.compactContentPadding * 2 + RHSpacing.small)
        const actionButtonsRowWidth = actionButtonWidths.summedValue +
            RHSpacing.medium * Math.max(0, actionButtons.length - 1)
        
        bounds
            .rectangleWithHeight(this.titleLabel)
            .assignedAsFrameOfView(this.titleLabel)
            .IF(this.closedWithoutContractInfoLabel.superview === this)
            .rectangleForNextRow(padding, this.closedWithoutContractInfoLabel)
            .assignedAsFrameOfView(this.closedWithoutContractInfoLabel)
            .ENDIF()
            .IF(this.offerersDataView.superview === this)
            .rectangleForNextRow(padding, this.offerersDataView)
            .assignedAsFrameOfView(this.offerersDataView)
            .IF(this.openingRequiredLabel.superview === this)
            .rectangleForNextRow(padding, this.openingRequiredLabel)
            .assignedAsFrameOfView(this.openingRequiredLabel)
            .ENDIF()
            .ELSE_IF(this._partSections.length > 0)
            .rectangleForNextRow(padding)
            .framesByDistributingViewsAsColumn(
                this._partSections,
                padding * 1.5,
                this._partSections
            )
            .lastElement
            .ELSE_IF(this.emptyStateLabel.superview === this)
            .rectangleForNextRow(padding, padding * 3)
            .assignedAsFrameOfView(this.emptyStateLabel)
            .ENDIF()
            .IF(actionButtons.length > 0)
            .rectangleForNextRow(padding, RHButtons.compactHeight)
            .IF(actionButtonsRowWidth <= bounds.width)
            .rectangleWithWidth(actionButtonsRowWidth, 0.5)
            .distributeViewsAlongWidth(actionButtons, nil, RHSpacing.medium, actionButtonWidths)
            .ELSE()
            .TRANSFORM(frame => frame.framesByDistributingViewsAsColumn(actionButtons, RHSpacing.medium, RHButtons.compactHeight).boundingBox())
            .ENDIF()
            .ENDIF()

    }


    static readonly _openOffersConfirmationText =
        "Kas soovid pakkumused avada? Pakkujaid teavitatakse pakkumuste avamisest. " +
        "Pärast avamist ei saa pakkumusi enam sulgeda."

    static readonly _openOffersButtonDefaultTitle = "Ava pakkumused ja teavita pakkujaid"
    static readonly _openOffersButtonBusyTitle = "Pakkumusi avatakse..."


    static _detailsTextForOffer(offer: Pick<RHProcurementOffer, "submittedAt" | "submittedBy">): string {
        const submitterName = RHOffersEvaluationView._submitterName(offer)
        const submittedAt = formatRHDateTimeValue(offer.submittedAt)
        if (!submittedAt) {
            return submitterName
        }
        return `${submitterName}\n${submittedAt}`
    }


    static _submitterName(offer: Pick<RHProcurementOffer, "submittedBy">): string {
        const displayName = populatedValueOfReference(offer.submittedBy)?.displayName
        if (typeof displayName === "string" && displayName.trim()) {
            return displayName.trim()
        }
        return "Nimi pole määratud"
    }

}


// ─────────────────────────────────────────────────────────────────────────────
// One section per procurement part, shown during evaluation (post-deadline)
// ─────────────────────────────────────────────────────────────────────────────

class RHPartOfferEvaluationSection extends RHPanelView {
    
    readonly partView: RHProcurementPartView

    readonly documentsHeaderView = new PartOfferDocumentsHeaderView().configuredWithObject({
        downloadButton: {
            controlEventTargetAccumulator: {
                PrimaryActionTriggered: () => this._downloadPartOfferFilesAsZip(this._partOffers)
            }
        }
    })
    
    readonly partOffersDataView = new RHDataView<RHProcurementPartOffer>()
        .configuredWithObject({
            tableView: { allRowsHaveEqualHeight: NO },
            hasResizableColumns: NO,
            isSearchAvailable: NO,
            maximumIntrinsicContentHeight: RHOffersEvaluationViewTokens.partOffersTableMaximumHeight
        })
        .addedAsSubviewToView(this)
    
    readonly emptyLabel = new UITextView()
        .configuredWithObject({
            text: "Sellele osale ei esitatud pakkumusi",
            textColor: RHColor.textSecondary,
            backgroundColor: UIColor.transparentColor,
            style: { fontSize: "14px", fontStyle: "italic", padding: "12px 0" }
        })
        .addedAsSubviewToView(this)
    
    // Footer — own finalize button / finalized banner per part, so the
    // procurer can lock in one part's evaluation while leaving others open.
    readonly finalizeButton = new RHButton()
        .configuredWithObject({
            titleLabel: { text: RHOffersEvaluationViewTokens.finalizeButtonTitle },
            variant: RHOffersEvaluationViewTokens.finalizeButtonVariant,
            contentPadding: RHButtons.compactContentPadding,
            style: {
                fontSize: RHButtons.compactFontSize
            },
            controlEventTargetAccumulator: {
                PrimaryActionTriggered: () => {
                    this._finalizePartEvaluationAndNotifyOfferers()
                }
            }
        })
    
    readonly finalizedInfoLabel = new RHAlertBannerView({
        alertType: RHAlertBannerView.type.Success,
        textPresentation: RHAlertBannerView.textPresentation.Outcome
    })

    readonly changeDecisionButton = new RHButton().configuredWithObject({
        titleLabel: { text: "Muuda otsust" }, variant: RHButtons.exitUtilityVariant,
        contentPadding: RHButtons.compactContentPadding,
        style: { fontSize: RHButtons.compactFontSize },
        controlEventTargetAccumulator: { PrimaryActionTriggered: () => { this._requestDecisionChange() } }
    })
    
    readonly closedNoWinnerInfoLabel = new RHAlertBannerView({
        alertType: RHAlertBannerView.type.Warning,
        textPresentation: RHAlertBannerView.textPresentation.Outcome
    })

    // Secondary action, shown above the finalize row only when this part's
    // eligible offers are tied at the lowest price — spins off a directed
    // child procurement scoped to this part instead of picking a winner here.
    readonly createReconciliationButton = new RHButton()
        .configuredWithObject({
            titleLabel: { text: "Loo täpsustav hange" },
            variant: RHButtons.compactPrimaryVariant,
            contentPadding: RHButtons.compactContentPadding,
            style: {
                fontSize: RHButtons.compactFontSize
            },
            controlEventTargetAccumulator: {
                PrimaryActionTriggered: () => {
                    this._requestReconciliationProcurement()
                }
            }
        })

    // Replaces the finalize row while this part is locked pending a
    // reconciliation child procurement's own winner decision.
    readonly reconciliationLockedInfoLabel = new RHAlertBannerView({
        alertType: RHAlertBannerView.type.Warning,
        textPresentation: RHAlertBannerView.textPresentation.Outcome
    })

    readonly abandonReconciliationButton = new RHButton()
        .configuredWithObject({
            titleLabel: { text: "Loobu täpsustamisest" },
            variant: RHOffersEvaluationViewTokens.finalizeButtonVariant,
            contentPadding: RHButtons.compactContentPadding,
            style: {
                fontSize: RHButtons.compactFontSize
            },
            controlEventTargetAccumulator: {
                PrimaryActionTriggered: () => {
                    this._confirmAbandonReconciliation()
                }
            }
        })

    // Cancels this part outright, regardless of finalize/reconciliation
    // state — shown alongside whichever of the buttons/banners above is
    // currently active, as long as the part itself isn't already finalized
    // or cancelled. Even reachable while reconciliationLockedInfoLabel is
    // shown: cancelling a locked part cascades to the live reconciliation
    // child procurement first (server-enforced, see CloseProcurementPartWithoutContract).
    readonly closePartWithoutContractButton = new RHButton()
        .configuredWithObject({
            variant: RHButtons.destructiveUtilityVariant,
            contentPadding: RHButtons.compactContentPadding,
            style: {
                fontSize: RHButtons.compactFontSize
            },
            controlEventTargetAccumulator: {
                PrimaryActionTriggered: () => {
                    this._requestPartClosureWithoutContract()
                }
            }
        })

    _part: RHProcurementPart
    numberOfRootProcurementParts?: number
    isPartClosedWithoutContractInRoundHistory = NO
    additionalActionButton?: RHButton
    _partCount: number | undefined
    _isEvaluationFinalizable: boolean
    _procurementCapabilities?: RHProcurementCapabilities
    _procurement: RHProcurement | undefined
    _createReconciliationDidRequest?: (procurementId: string, partId: string) => void
    _cancelPartDidRequest?: (procurementId: string, partId: string) => void
    
    override _focusManager = new RHFocusManager(() => {
        const items: UIView[] = []
        if (this.partOffersDataView.superview === this) {
            items.push(this.documentsHeaderView.downloadButton)
            items.push(this.partOffersDataView.searchTextField)
            items.push(this.partOffersDataView._gridContainerView)
        }
        for (const button of [this.changeDecisionButton, this.finalizeButton, this.createReconciliationButton,
            this.abandonReconciliationButton, this.closePartWithoutContractButton, this.additionalActionButton]) {
            if (button?.superview === this) {
                items.push(button)
            }
        }
        return items
    })
    _evaluationDidChange: () => void
    _evaluationDidFinalize: () => void
    _selectedOfferId: string | undefined
    _cellDataMap = new Map<RHCheckbox, RHProcurementPartOffer>()
    _partOffers: RHProcurementPartOffer[] = []
    _pendingEvaluationChange?: Promise<boolean>
    _hasEvaluationSaveFailure = NO
    _isFinalizingEvaluation = NO
    
    _persistedEvaluationSnapshots = new Map<string, {
        meetsRequirements: RHProcurementPartOffer["meetsRequirements"]
        decision: RHProcurementPartOffer["decision"]
        evaluationNotes: RHProcurementPartOffer["evaluationNotes"]
    }>()
    
    // Reused across this section's rows: the "Tulem" column button opens this
    // single dialog to add / edit / view the focused result note (evaluationNotes).
    _evaluationNotesDialog?: EvaluationNotesDialogView
    
    /**
     * Part offer IDs with a local edit that has not yet been confirmed by the
     * server (a debounced note still waiting to fire, or any evaluation write
     * currently in flight). mergeFreshPartOffers must not overwrite these —
     * the incoming server snapshot predates the edit and would otherwise
     * stomp on text the user is actively typing, or briefly revert an
     * optimistic winner selection.
     */
    _pendingLocalEditOfferIds = new Set<string>()
    
    constructor(
        part: RHProcurementPart,
        isEvaluationFinalizable: boolean,
        evaluationDidChange: () => void,
        evaluationDidFinalize: () => void,
        procurement?: RHProcurement,
        procurementCapabilities?: RHProcurementCapabilities,
        createReconciliationDidRequest?: (procurementId: string, partId: string) => void,
        cancelPartDidRequest?: (procurementId: string, partId: string) => void,
        readonly isReadOnly = NO,
        public hasNextRound = NO
    ) {
        super()
        this._part = part
        this._isEvaluationFinalizable = isEvaluationFinalizable
        this._evaluationDidChange = evaluationDidChange
        this._evaluationDidFinalize = evaluationDidFinalize
        this._procurement = procurement
        this._procurementCapabilities = procurementCapabilities
        this._createReconciliationDidRequest = createReconciliationDidRequest
        this._cancelPartDidRequest = cancelPartDidRequest
        this._partCount = procurement?.parts?.length
        this.backgroundColor = UIColor.whiteColor

        // Part-specific finalize button title, e.g. "Lõpeta osa 2 hindamine ja teavita pakkujaid"
        const showsPartIdentity = RHProcurementPartDisplay.shouldShowPartIdentityForPartCount(
            this._partCount ?? 1
        )
        this.closePartWithoutContractButton.titleLabel.text = RHProcurementPartDisplay.closeWithoutContractActionButtonText(
            part, this._partCount
        )
        this.finalizedInfoLabel.configureBannerWithObject({
            text: RHProcurementPartDisplay.finalizedEvaluationText(this._partCount)
        })
        this.closedNoWinnerInfoLabel.configureBannerWithObject({
            text: RHProcurementPartDisplay.closedEvaluationWithoutWinnerText(this._partCount)
        })
        this.reconciliationLockedInfoLabel.configureBannerWithObject({
            text: RHProcurementPartDisplay.evaluationMovedToNextRoundText
        })
        if (showsPartIdentity) {
            this.abandonReconciliationButton.titleLabel.text = `Loobu osa ${part.partNumber} täpsustamisest`
            this.finalizeButton.titleLabel.text = `Lõpeta osa ${part.partNumber} hindamine ja teavita pakkujaid`
        }
        
        this.partView = new RHProcurementPartView()
            .addedAsSubviewToView(this) as RHProcurementPartView
        this.partView.configureWithPartData(part, YES, undefined, this._partCount)
        this.emptyLabel.text = RHProcurementPartDisplay.noPartOffersText(this._partCount)
        
        this.partOffersDataView.descriptors = this._partOffersTableDescriptors()
        this.partOffersDataView.tableHeaderView.configureWithObject({
            cellWeights: RHOffersEvaluationViewTokens.partOffersTableColumnWeights,
            intrinsicContentHeight: () => 64
        })
        this._configurePartOffersTableHeaderCells()
        const newReusablePartOfferRowViewForIdentifier = this.partOffersDataView.tableView.newReusableViewForIdentifier
        this.partOffersDataView.tableView.configureWithObject({
            cellWeights: RHOffersEvaluationViewTokens.partOffersTableColumnWeights,
            newReusableViewForIdentifier: (identifier: string, rowIDIndex: number) => {
                const rowView = newReusablePartOfferRowViewForIdentifier(identifier, rowIDIndex) as RowView
                rowView.configureWithObject({
                    cellWeights: RHOffersEvaluationViewTokens.partOffersTableColumnWeights
                })
                return rowView
            }
        })
        this.partOffersDataView.tableView.allRowsHaveEqualHeight = NO
        this.partOffersDataView.tableView.usesVirtualLayoutingForIntrinsicSizing = NO
        this.partOffersDataView.configureWithObject({
            backgroundColor: UIColor.whiteColor,
            style: {
                border: "none",
                boxShadow: "none"
            }
        })
        this.emptyLabel.hidden = YES
    }
    
    set procurementCapabilities(capabilities: RHProcurementCapabilities | undefined) {
        this._procurementCapabilities = capabilities
        this.partOffersDataView.data = [...this.partOffersDataView.data]
        this.setNeedsLayoutUpToRootView()
    }
    
    get _canEvaluatePartOffers(): boolean {
        if (!this._procurementCapabilities || this.isReadOnly) {
            return NO
        }
        return this._procurementCapabilities.canEvaluate === YES
    }
    
    get _canSelectWinner(): boolean {
        if (!this._procurementCapabilities || this.isReadOnly) {
            return NO
        }
        return this._procurementCapabilities.canSelectWinner === YES
    }
    
    get _canFinalizePartEvaluation(): boolean {
        if (!this._procurementCapabilities || this.isReadOnly) {
            return NO
        }
        return this._procurementCapabilities.canFinalizeOrCancel === YES
    }
    
    /**
     * Whether this specific part has already been individually finalized by
     * the procurer (FinalizePartEvaluationAndNotify). Once finalized, the winner
     * and evaluation fields for this part can no longer be changed — the
     * server enforces this independently, this is purely the client-side
     * reflection of that lock.
     */
    get _isPartFinalized(): boolean {
        const partIdString = this._part._id.toString()
        return (this._procurement?.finalizedPartIds ?? []).includes(partIdString)
    }

    get _isPartClosedWithoutContract(): boolean {
        return this.isPartClosedWithoutContractInRoundHistory ||
            this._procurement?.status === "closed-without-contract" ||
            (this._procurement?.closedWithoutContractPartIds ?? []).includes(this._part._id.toString())
    }
    
    get _canReopenPartEvaluation(): boolean {
        const partId = this._part._id.toString()
        return this._canSelectWinner && !this._isPartClosedWithoutContract && this._isPartFinalized && this._partHasWinner && !this.hasNextRound &&
            !this._isPartLockedForReconciliation &&
            (this._procurement?.pendingContractPartIds ?? []).includes(partId)
    }
    
    /**
     * Whether this part's winner/evaluation controls should currently accept
     * input: the procurement as a whole must be in its post-deadline,
     * still-published evaluation window (_isEvaluationFinalizable), AND this
     * specific part must not already be finalized. Replaces every direct use
     * of _isEvaluationFinalizable for gating interactivity in this section.
     */
    get _isPartEvaluationEditable(): boolean {
        return this._isEvaluationFinalizable &&
            !this._isFinalizingEvaluation &&
            !this._isPartClosedWithoutContract &&
            !this._isPartFinalized &&
            !this._isPartLockedForReconciliation &&
            this._canEvaluatePartOffers
    }

    get _isWinnerSelectionEditable(): boolean {
        return this._isEvaluationFinalizable &&
            !this._isFinalizingEvaluation &&
            !this._isPartClosedWithoutContract &&
            !this._isPartFinalized &&
            !this._isPartLockedForReconciliation &&
            this._canSelectWinner
    }

    get _isPartFinalizationEditable(): boolean {
        return this._isEvaluationFinalizable &&
            !this._isPartClosedWithoutContract &&
            !this._isPartFinalized &&
            !this._isPartLockedForReconciliation &&
            this._canFinalizePartEvaluation
    }

    /**
     * Whether the cancel action should be offered for this part. Unlike
     * _isPartFinalizationEditable, this deliberately does NOT exclude a
     * reconciliation-locked part — cancelling one is allowed and cascades to
     * the live reconciliation child procurement server-side. _isPartFinalized
     * already excludes an already-cancelled part too, since cancelling a
     * part (in the mixed case) also adds it to finalizedPartIds. Restricted
     * to multi-part procurements, matching CloseProcurementPartWithoutContract's own guard.
     */
    get _isPartClosureWithoutContractEditable(): boolean {
        return this._isEvaluationFinalizable &&
            !this._isPartClosedWithoutContract &&
            !this._isPartFinalized &&
            this._canFinalizePartEvaluation &&
            (this._partCount ?? 0) > 1
    }

    /**
     * Whether a reconciliation child procurement currently owns this part's
     * winner decision — the server enforces this independently (see
     * reconciliationLockedPartIds), this is purely the client-side
     * reflection of that lock.
     */
    get _isPartLockedForReconciliation(): boolean {
        const partIdString = this._part._id.toString()
        return (this._procurement?.reconciliationLockedPartIds ?? []).includes(partIdString)
    }

    /**
     * Whether "Loo täpsustav hange" should be offered: this part's own
     * winner decision must still be open here. No longer requires a price
     * collision — the procurer may start a reconciliation round for any
     * reason, inviting any subset of the part's bidders.
     */
    get _isReconciliationCreationEditable(): boolean {
        return RHFeatureFlags.reconciliationProcurementCreationIsVisible &&
            this._isWinnerSelectionEditable
    }

    /**
     * Whether this part currently has a recorded winner — drives which of
     * the two finalized banners (success / no-winner-warning) is shown once
     * _isPartFinalized is active.
     */
    get _partHasWinner(): boolean {
        const partIdString = this._part._id.toString()
        return !!this._procurement?.partWinnerOfferIds?.[partIdString]
    }
    
    _configurePartOffersTableHeaderCells() {
        this.partOffersDataView.tableHeaderView.cells[2].customView = this.documentsHeaderView
        const meetsRequirementsHeaderCell = this.partOffersDataView.tableHeaderView.cells[
            3
            ]
        const buttonView = meetsRequirementsHeaderCell?.buttonView
        if (!buttonView) {
            return
        }
        
        buttonView.contentPadding = 2
        
        const titleLabel = buttonView.titleLabel
        titleLabel.isSingleLine = NO
        titleLabel.style.whiteSpace = "normal"
        titleLabel.style.lineHeight = RHOffersEvaluationViewTokens.partOffersMultilineHeaderLineHeight
        titleLabel.textElementView.style.whiteSpace = "normal"
        titleLabel.textElementView.style.lineHeight = RHOffersEvaluationViewTokens.partOffersMultilineHeaderLineHeight
    }
    
    configureWithPartOffers(partOffers: RHProcurementPartOffer[]) {
        this._partOffers = partOffers
        this.documentsHeaderView.downloadButton.enabled = !this._isDownloadingPartOfferFiles && partOffers.some(
            partOffer => this._downloadableFileIdsForPartOffer(partOffer).length > 0
        )
        this._selectedOfferId = partOffers.find(p => p.decision === "chosenAsWinner")?._id?.toString()
        
        this._persistedEvaluationSnapshots.clear()
        for (const partOffer of partOffers) {
            const partOfferId = partOffer._id?.toString()
            if (partOfferId) {
                this._persistedEvaluationSnapshots.set(partOfferId, {
                    meetsRequirements: partOffer.meetsRequirements,
                    decision: partOffer.decision,
                    evaluationNotes: partOffer.evaluationNotes
                })
            }
        }
        
        if (partOffers.length === 0) {
            this.partOffersDataView.removeFromSuperview()
            this.emptyLabel.hidden = NO
        }
        else {
            this.partOffersDataView.addedAsSubviewToView(this)
            this.emptyLabel.hidden = YES
            this.partOffersDataView.data = partOffers
        }
        this.setNeedsLayoutUpToRootView()
    }
    
    _markPartOfferAsLocallyDirty(partOfferId: string | undefined) {
        if (partOfferId) {
            this._pendingLocalEditOfferIds.add(partOfferId)
        }
    }
    
    _clearPartOfferLocalDirtyFlag(partOfferId: string | undefined) {
        if (partOfferId) {
            this._pendingLocalEditOfferIds.delete(partOfferId)
        }
    }
    
    /**
     * Merges a fresh server snapshot of this part's offers into the existing
     * `_partOffers` array in place, instead of replacing it wholesale. Used
     * by the parent view's push-triggered refresh so a notification never
     * tears down this section's table or loses scroll position / textarea
     * focus.
     *
     * Any part offer with a pending local edit (see _pendingLocalEditOfferIds)
     * is left untouched — the server snapshot predates that edit and would
     * otherwise overwrite text the user is actively typing, or briefly
     * revert an optimistic winner selection before our own write resolves.
     */
    mergeFreshPartOffers(freshPartOffers: (RHProcurementPartOffer & { offererCompanyName: string })[]) {
        const freshPartOffersById = new Map<string, RHProcurementPartOffer & { offererCompanyName: string }>()
        for (const freshPartOffer of freshPartOffers) {
            const freshPartOfferId = freshPartOffer._id?.toString()
            if (freshPartOfferId) {
                freshPartOffersById.set(freshPartOfferId, freshPartOffer)
            }
        }
        
        const mergedPartOffers: RHProcurementPartOffer[] = []
        
        for (const existingPartOffer of this._partOffers) {
            const existingPartOfferId = existingPartOffer._id?.toString()
            
            if (existingPartOfferId && this._pendingLocalEditOfferIds.has(existingPartOfferId)) {
                // Local edit still in flight — keep local state authoritative.
                mergedPartOffers.push(existingPartOffer)
                freshPartOffersById.delete(existingPartOfferId)
                continue
            }
            
            if (!existingPartOfferId) {
                mergedPartOffers.push(existingPartOffer)
                continue
            }
            
            const freshMatch = freshPartOffersById.get(existingPartOfferId)
            if (!freshMatch) {
                // No longer present server-side (e.g. cancelled) — drop it.
                continue
            }
            
            Object.assign(existingPartOffer, freshMatch)
            mergedPartOffers.push(existingPartOffer)
            freshPartOffersById.delete(existingPartOfferId)
            this._persistedEvaluationSnapshots.set(existingPartOfferId, {
                meetsRequirements: existingPartOffer.meetsRequirements,
                decision: existingPartOffer.decision,
                evaluationNotes: existingPartOffer.evaluationNotes
            })
        }
        
        // Anything left over is a part offer that did not exist locally yet.
        for (const newPartOffer of freshPartOffersById.values()) {
            mergedPartOffers.push(newPartOffer)
            const newPartOfferId = newPartOffer._id?.toString()
            if (newPartOfferId) {
                this._persistedEvaluationSnapshots.set(newPartOfferId, {
                    meetsRequirements: newPartOffer.meetsRequirements,
                    decision: newPartOffer.decision,
                    evaluationNotes: newPartOffer.evaluationNotes
                })
            }
        }
        
        this._partOffers = mergedPartOffers
        this.documentsHeaderView.downloadButton.enabled = !this._isDownloadingPartOfferFiles && mergedPartOffers.some(
            partOffer => this._downloadableFileIdsForPartOffer(partOffer).length > 0
        )
        this._selectedOfferId = mergedPartOffers.find(
            partOfferItem => partOfferItem.decision === "chosenAsWinner"
        )?._id?.toString()
        
        if (mergedPartOffers.length === 0) {
            this.partOffersDataView.removeFromSuperview()
            this.emptyLabel.hidden = NO
        }
        else {
            this.partOffersDataView.addedAsSubviewToView(this)
            this.emptyLabel.hidden = YES
            const firstVisibleRowIndex = this.partOffersDataView.tableView
                .indexesForVisibleRows(0)
                .firstElement
            if (firstVisibleRowIndex !== undefined) {
                const firstVisibleRow = this.partOffersDataView.tableView
                    .visibleRowWithIndex(firstVisibleRowIndex) as CBTableRowView<RHProcurementPartOffer> | undefined
                if (firstVisibleRow?.data && mergedPartOffers.includes(firstVisibleRow.data)) {
                    this.partOffersDataView.keepRowStaticDuringNextUpdate(firstVisibleRow)
                }
            }
            this.partOffersDataView.data = [...mergedPartOffers]
        }
        this.setNeedsLayoutUpToRootView()
    }
    
    
    _openEvaluationNotesDialog(partOffer: RHProcurementPartOffer) {
        if (!this._evaluationNotesDialog) {
            this._evaluationNotesDialog = new EvaluationNotesDialogView()
        }
        this._evaluationNotesDialog.showForNotes(
            partOffer.evaluationNotes ?? "",
            this._isPartEvaluationEditable,
            (committedNotes: string) => {
                void this._enqueueEvaluationChange(async () => {
                    partOffer.evaluationNotes = committedNotes
                    this.partOffersDataView.data = [...this.partOffersDataView.data]
                    await this._persistPartOfferEvaluation(partOffer)
                    this._evaluationDidChange()
                })
            }
        )
    }
    
    
    _resultActionButtonTitle(partOffer: RHProcurementPartOffer): string {
        if (!this._isPartEvaluationEditable) {
            return "Vaata"
        }
        const hasNotes = (partOffer.evaluationNotes ?? "").trim().length > 0
        if (hasNotes) {
            return "Muuda"
        }
        return "Lisa"
    }
    
    // ── Part-offer document download ──────────────────────────────────────────
    // Guards against a second request while the server builds + streams the ZIP.
    _isDownloadingPartOfferFiles = NO
    
    /**
     * Every downloadable file ID for a part offer: the freely-attached files plus
     * every file in each filled required-document slot. Mirrors exactly the count shown
     * in the "Dokumendid" cell, so the ZIP contains what the cell advertises.
     */
    _downloadableFileIdsForPartOffer(partOffer: RHProcurementPartOffer): string[] {
        const fileIds: string[] = []
        
        for (const regularFile of partOffer.files ?? []) {
            const fileId = referenceId(regularFile)
            if (fileId) {
                fileIds.push(fileId)
            }
        }
        
        const requiredDocumentFileIds = partOffer.requiredDocumentFileIds ?? {}
        for (const requiredDocument of (this._part.requiredDocuments ?? [])) {
            fileIds.push(...[requiredDocumentFileIds[requiredDocument._id] ?? []].flat())
        }
        
        return fileIds
    }
    
    async _downloadPartOfferFilesAsZip(partOffers: RHProcurementPartOffer[]) {
        if (this._isDownloadingPartOfferFiles) {
            return
        }
        
        const partOfferIds = partOffers.filter(
            partOffer => this._downloadableFileIdsForPartOffer(partOffer).length > 0
        ).map(partOffer => referenceId(partOffer)).filter((partOfferId): partOfferId is string => !!partOfferId)
        if (partOfferIds.length === 0) {
            return
        }
        
        this._isDownloadingPartOfferFiles = YES
        this.documentsHeaderView.downloadButton.enabled = NO
        try {
            const download = await RHCore.sharedInstance.offerStore.retrieveFilesDownloadForPartOffers(partOfferIds)
            await RHFileListView.downloadFileFromUrl(download.url, download.fileName)
        }
        catch (error) {
            console.error("[RHOffersEvaluationView] Part-offer ZIP download failed:", error)
            CBDialogViewShower.alert("Failide allalaadimine ebaõnnestus. Palun proovi uuesti.")
        }
        finally {
            this._isDownloadingPartOfferFiles = NO
            this.documentsHeaderView.downloadButton.enabled = this._partOffers.some(
                partOffer => this._downloadableFileIdsForPartOffer(partOffer).length > 0
            )
        }
    }
    
    _partOffersTableDescriptors(): CBColumnDescriptor<RHProcurementPartOffer>[] {
        const descriptors: CBColumnDescriptor<RHProcurementPartOffer>[] = [
            {
                title: "Pakkuja",
                keyPath: "offererCompanyName",
                minimumWidth: RHOffersEvaluationViewTokens.partOffersOffererColumnMinimumWidth,
                defaultTitle: "Tundmatu ettevõte",
                allowSorting: YES,
                titleLabelAlignment: UITextView.textAlignment.left,
                cellLabelAlignment: UITextView.textAlignment.left,
                updateCell: (cellView: CellView, dataItem: EvaluationPartOffer) => {
                    cellView.userInteractionEnabled = this._isPartEvaluationEditable
                    // offererCompany is populated on the parent offer; we denormalise
                    // the company name onto part offers via the parent offer join
                    const conflictReasons = dataItem.conflictOfInterestReasons ?? []
                    let companyName = dataItem.offererCompanyName ?? "Tundmatu ettevõte"
                    const submissionDetails = (dataItem as EvaluationPartOffer)
                        .offerSubmissionDetailsText
                    if (submissionDetails) {
                        companyName += `\n${submissionDetails}`
                    }
                    if (conflictReasons.length > 0) {
                        companyName += "\n⚠ Võimalik huvide konflikt"
                    }
                    cellView.configureAsMultilineTextPreservingLineBreaks()
                    cellView.titleLabel!.text = RHTextSanitizer.escapedHTML(companyName).replace(/\r?\n/g, "<br>")
                    cellView.hoverText = conflictReasons
                        .map(reason => RHConflictOfInterestReasonTranslations[reason])
                        .join("\n")
                }
            },
            {
                title: "Maksumus (KM-ta)",
                keyPath: "price.amount",
                allowSorting: YES,
                dataType: "number" as const,
                titleLabelAlignment: UITextView.textAlignment.right,
                cellLabelAlignment: UITextView.textAlignment.right,
                updateCell: (cellView: CellView, dataItem: RHProcurementPartOffer) => {
                    cellView.userInteractionEnabled = this._isPartEvaluationEditable
                    // _decryptionFailed is set by the server when the sealed-bid envelope could not be decrypted.
                    if (dataItem._decryptionFailed) {
                        cellView.titleLabel!.configureWithObject({
                            text: "???",
                            style: { fontWeight: RHFont.weightRegular }
                        })
                    }
                    else if (dataItem.price?.amount !== undefined) {
                        const formattedPrice = RHEuroFormatter.formattedCurrency(dataItem.price.amount, {
                            currency: dataItem.price.currency || "EUR",
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 2
                        })
                        cellView.titleLabel!.configureWithObject({
                            text: formattedPrice,
                            style: {
                                fontWeight: IF(RHPartOfferEvaluationSection._isLowestCompliantOffer(
                                    dataItem,
                                    this._partOffers
                                ))(() => RHFont.weightBold).ELSE(() => RHFont.weightRegular)
                            }
                        })
                    }
                    else {
                        cellView.titleLabel!.configureWithObject({
                            text: "-",
                            style: { fontWeight: RHFont.weightRegular }
                        })
                    }
                }
            },
            CBDataViewCellDescriptorWithObject({
                title: "Dokumendid",
                minimumWidth: RHTableColumnMinimumWidths.tableAction + RHButtons.tableActionHeight,
                titleLabelAlignment: UITextView.textAlignment.center,
                cellLabelAlignment: UITextView.textAlignment.center,
                updateCell: (cellView: CellView, dataItem: RHProcurementPartOffer) => {
                    const hasFiles = this._downloadableFileIdsForPartOffer(dataItem).length > 0
                    
                    // Only a non-empty cell is an interactive download link; an
                    // empty one is plain, muted "Puuduvad" text.
                    cellView.isAButton = hasFiles
                    cellView.userInteractionEnabled = hasFiles
                    
                    cellView.titleLabel!.configureWithObject({
                        text: IF(hasFiles)(() => "Lae alla (zip)").ELSE(() => "Puuduvad"),
                        textColor: IF(hasFiles)(() => RHColor.primary).ELSE(() => RHColor.textSecondary),
                        textAlignment: UITextView.textAlignment.center
                    })
                },
                buttonWasPressed: (_cellView: CellView, dataItem: RHProcurementPartOffer) => {
                    void this._downloadPartOfferFilesAsZip([dataItem])
                }
            }),
            CBDataViewCellDescriptorWithObject<RHProcurementPartOffer, MeetsRequirementsToggleView>({
                title: "Vastab hanke-<br>tingimustele",
                keyPath: "meetsRequirements",
                allowSorting: YES,
                titleLabelAlignment: UITextView.textAlignment.center,
                cellLabelAlignment: UITextView.textAlignment.center,
                initCustomView: () => new MeetsRequirementsToggleView(),
                updateCell: (cellView, dataItem: RHProcurementPartOffer) => {
                    const toggleView = cellView.customView
                    toggleView.editable = this._isPartEvaluationEditable
                    toggleView.value = dataItem.meetsRequirements
                    // Two separate buttons: "Jah" sets YES, "Ei" sets NO — never a blind
                    // flip — so an accidental tap can no longer invert an already-correct verdict.
                    toggleView.valueDidChange = (newMeetsRequirements: boolean) => {
                        if (!this._isPartEvaluationEditable) {
                            return
                        }
                        void this._enqueueEvaluationChange(async () => {
                            if (dataItem.meetsRequirements === newMeetsRequirements) {
                                return
                            }
                            dataItem.meetsRequirements = newMeetsRequirements
                            const previousWinnerId = this._selectedOfferId
                            const didClearWinner = !newMeetsRequirements && dataItem._id?.toString() === previousWinnerId
                            if (didClearWinner) {
                                this._selectedOfferId = undefined
                                dataItem.decision = "undecided"
                            }
                            this.partOffersDataView.data = [...this.partOffersDataView.data]
                            await this._persistPartOfferEvaluation(dataItem, () => {
                                this._selectedOfferId = previousWinnerId
                            })
                            if (didClearWinner && this._procurement?.partWinnerOfferIds) {
                                delete this._procurement.partWinnerOfferIds[this._part._id.toString()]
                            }
                            this._evaluationDidChange()
                        })
                    }
                }
            }),
            CBDataViewCellDescriptorWithObject<RHProcurementPartOffer, RHTableActionButtonView>({
                // "Tulem" — opens the result-note editor dialog instead of editing
                // inline. The button label reflects state: Lisa (no note yet) /
                // Muuda (a note exists) / Vaata (evaluation locked, read-only).
                title: "Tulem",
                keyPath: "",
                minimumWidth: RHOffersEvaluationViewTokens.partOffersResultActionWidth,
                allowSorting: NO,
                titleLabelAlignment: UITextView.textAlignment.center,
                cellLabelAlignment: UITextView.textAlignment.center,
                initCustomView: () => new RHTableActionButtonView().configuredWithObject({
                    preferredButtonWidth: RHOffersEvaluationViewTokens.partOffersResultActionWidth
                }),
                updateCell: (cellView, dataItem: RHProcurementPartOffer) => {
                    const actionButtonView = cellView.customView
                    actionButtonView.text = this._resultActionButtonTitle(dataItem)
                    actionButtonView.buttonWasPressed = () => {
                        this._openEvaluationNotesDialog(dataItem)
                    }
                }
            }),
            CBDataViewCellDescriptorWithObject<RHProcurementPartOffer, RHCheckbox>({
                title: "Edukas",
                minimumWidth: RHOffersEvaluationViewTokens.partOffersWinnerColumnMinimumWidth,
                allowSorting: NO,
                initCustomView: () => {
                    const checkbox = new RHCheckbox()
                    checkbox.viewHTMLElement.tabIndex = -1
                    checkbox.checkbox.viewHTMLElement.tabIndex = -1
                    return checkbox
                },
                initCell: cellView => {
                    // noinspection JSConstantReassignment
                    // @ts-ignore
                    cellView.controlEventTargetAccumulator.PointerUpInside.EnterDown.SpaceDown = async () => {
                        if (!this._isWinnerSelectionEditable) {
                            return
                        }
                        const dataItem = this._cellDataMap.get(cellView.customView)
                        if (!dataItem) {
                            return
                        }
                        const partOfferId = dataItem._id?.toString()
                        if (!partOfferId) {
                            return
                        }
                        await this._enqueueEvaluationChange(async () => {
                            if (dataItem.meetsRequirements !== YES) {
                                return
                            }
                            const wasWinner = partOfferId === this._selectedOfferId
                            const previousWinnerId = this._selectedOfferId
                            const affectedPartOfferIds = this._partOffers
                                .map(offerItem => offerItem._id?.toString())
                                .filter((offerItemId): offerItemId is string => !!offerItemId)
                            for (const affectedPartOfferId of affectedPartOfferIds) {
                                this._markPartOfferAsLocallyDirty(affectedPartOfferId)
                            }
                            this._applyWinnerSelection(
                                IF(wasWinner)(() => undefined).ELSE(() => partOfferId),
                                dataItem
                            )
                            try {
                                if (wasWinner) {
                                    await RHCore.sharedInstance.offerStore.updatePartOfferEvaluation({
                                        partOfferId: previousWinnerId!,
                                        meetsRequirements: dataItem.meetsRequirements,
                                        evaluationNotes: dataItem.evaluationNotes ?? "",
                                        decision: "undecided"
                                    })
                                }
                                else {
                                    await RHCore.sharedInstance.offerStore.setProcurementPartWinner(
                                        dataItem.procurement?._id?.toString()
                                            ?? dataItem.procurement?.toString(),
                                        partOfferId
                                    )
                                }
                                this._hasEvaluationSaveFailure = NO
                                for (const partOffer of this._partOffers) {
                                    const snapshot = this._persistedEvaluationSnapshots.get(partOffer._id + "")
                                    if (snapshot) {
                                        snapshot.decision = partOffer.decision
                                    }
                                }
                                this._evaluationDidChange()
                            }
                            catch (error) {
                                this._applyWinnerSelection(previousWinnerId, dataItem)
                                throw error
                            }
                            finally {
                                for (const affectedPartOfferId of affectedPartOfferIds) {
                                    this._clearPartOfferLocalDirtyFlag(affectedPartOfferId)
                                }
                            }
                        })
                    }
                },
                updateCell: (cellView, dataItem: RHProcurementPartOffer) => {
                    const canSelectWinner = this._isWinnerSelectionEditable && dataItem.meetsRequirements === YES
                    cellView.userInteractionEnabled = canSelectWinner
                    if (this._isWinnerSelectionEditable && dataItem.meetsRequirements !== YES) {
                        UITooltip.sharedInstance.attach(
                            cellView, "Esmalt hinda, kas pakkumus vastab hanketingimustele")
                    }
                    else if (
                        this._isEvaluationFinalizable &&
                        !this._isPartFinalized &&
                        !this._canSelectWinner
                    ) {
                        UITooltip.sharedInstance.attach(
                            cellView, "Selle rolliga ei saa pakkumust edukaks märkida")
                    }
                    else {
                        UITooltip.sharedInstance.detach(cellView)
                    }
                    this._cellDataMap.set(cellView.customView, dataItem)
                    cellView.customView.enabled = canSelectWinner
                    cellView.customView.userInteractionEnabled = NO
                    cellView.customView.selected = dataItem._id?.toString() === this._selectedOfferId
                }
            })
        ]

        return descriptors.map(descriptor => {
            if (!this.hasNextRound || !descriptor.initCustomView) {
                return descriptor
            }
            return CBDataViewCellDescriptorWithObject<RHProcurementPartOffer>({
                title: descriptor.title,
                minimumWidth: descriptor.minimumWidth,
                allowSorting: NO,
                titleLabelAlignment: UITextView.textAlignment.center,
                cellLabelAlignment: UITextView.textAlignment.center,
                updateCell: cellView => {
                    cellView.configureAsWrappingMultilineText()
                    cellView.titleLabel!.text = "Ei määratud"
                }
            })
        })
    }
    
    _applyWinnerSelection(newWinnerId: string | undefined, triggerDataItem: RHProcurementPartOffer) {
        this._selectedOfferId = newWinnerId
        for (const partOfferItem of this._partOffers) {
            partOfferItem.decision = IF(partOfferItem._id?.toString() === newWinnerId)(() =>
                "chosenAsWinner"
            ).ELSE(() =>
                "undecided"
            )
        }
        if (this._procurement) {
            const partId = triggerDataItem.partId?.toString()
            if (partId) {
                const partWinnerOfferIds = this._procurement.partWinnerOfferIds ?? {}
                if (newWinnerId) {
                    partWinnerOfferIds[partId] = newWinnerId
                }
                else {
                    delete partWinnerOfferIds[partId]
                }
                this._procurement.partWinnerOfferIds = partWinnerOfferIds
            }
        }
        for (const [checkbox, item] of this._cellDataMap) {
            checkbox.selected = item._id?.toString() === newWinnerId
        }
    }
    
    async _persistPartOfferEvaluation(
        partOffer: RHProcurementPartOffer,
        onError?: () => void
    ) {
        const partOfferId = partOffer._id?.toString()
        this._markPartOfferAsLocallyDirty(partOfferId)
        try {
            const savedPartOffer = await RHCore.sharedInstance.offerStore.updatePartOfferEvaluation({
                partOfferId: partOffer._id + "",
                meetsRequirements: partOffer.meetsRequirements,
                evaluationNotes: partOffer.evaluationNotes ?? "",
                decision: partOffer.decision ?? "undecided"
            })
            this._hasEvaluationSaveFailure = NO
            if (partOfferId) {
                this._persistedEvaluationSnapshots.set(partOfferId, {
                    meetsRequirements: savedPartOffer.meetsRequirements,
                    decision: savedPartOffer.decision,
                    evaluationNotes: savedPartOffer.evaluationNotes
                })
            }
        }
        catch (error) {
            const persisted = this._persistedEvaluationSnapshots.get(partOfferId ?? "")
            if (persisted) {
                partOffer.meetsRequirements = persisted.meetsRequirements
                partOffer.decision = persisted.decision
                partOffer.evaluationNotes = persisted.evaluationNotes
            }
            onError?.()
            this.partOffersDataView.data = [...this.partOffersDataView.data]
            throw error
        }
        finally {
            this._clearPartOfferLocalDirtyFlag(partOfferId)
        }
    }
    
    async _enqueueEvaluationChange(change: () => Promise<void>): Promise<boolean> {
        if (this._isFinalizingEvaluation) {
            return NO
        }
        const previousChange = this._pendingEvaluationChange
        const pendingChange = (async () => {
            if (previousChange && !await previousChange) {
                return NO
            }
            try {
                await change()
                return YES
            }
            catch (error) {
                this._hasEvaluationSaveFailure = YES
                console.error("Error saving evaluation:", error)
                let errorMessage = "Hindamise salvestamine ebaõnnestus. Kontrolli ühendust ja salvesta valik uuesti."
                if (error instanceof Error) {
                    errorMessage = error.message
                }
                CBDialogViewShower.alert(errorMessage)
                return NO
            }
        })()
        this._pendingEvaluationChange = pendingChange
        try {
            return await pendingChange
        }
        finally {
            if (this._pendingEvaluationChange === pendingChange) {
                this._pendingEvaluationChange = undefined
            }
        }
    }

    async _finalizePartEvaluationAndNotifyOfferers() {
        if (!this._procurement?._id || !this._isPartFinalizationEditable || this._isFinalizingEvaluation) {
            return
        }

        this._isFinalizingEvaluation = YES
        this.finalizeButton.enabled = NO
        this.finalizeButton.titleLabel.text = "Töötlemine…"
        try {
            if (this._pendingEvaluationChange && !await this._pendingEvaluationChange) {
                return
            }
            if (!this.isMemberOfViewTree || !this._isPartFinalizationEditable) {
                return
            }
            if (this._hasEvaluationSaveFailure) {
                CBDialogViewShower.alert("Mõni hindamise muudatus jäi salvestamata. Salvesta valik uuesti või värskenda lehte ja kontrolli tulemust enne hindamise lõpetamist.")
                return
            }
            const expectedWinningPartOfferId = this._selectedOfferId ?? null
        
            const showsPartIdentity = RHProcurementPartDisplay.shouldShowPartIdentityForPartCount(
                this.numberOfRootProcurementParts ?? this._partCount ?? 1
            )
            const questionText = IF(!expectedWinningPartOfferId)(() => {
                if (!showsPartIdentity) {
                    return "Ühtegi pakkumust pole edukaks märgitud — see hange jääb lepinguta. Pärast kinnitamist ei saa hindamist enam avada ega tulemust muuta. Kas soovid hindamise siiski lõpetada ja pakkujaid teavitada?"
                }
                return `Osa ${this._part.partNumber}: ühtegi pakkumust pole edukaks märgitud — see osa jääb lepinguta. Pärast kinnitamist ei saa selle osa hindamist enam avada ega tulemust muuta. Kas soovid selle osa hindamise siiski lõpetada ja sellele osale pakkumuse esitanud pakkujaid teavitada?`
            }).ELSE(() => {
                if (!showsPartIdentity) {
                    return "Kas oled kindel, et soovid hindamise lõpetada ja kõiki pakkujaid tulemustest teavitada?"
                }
                return `Kas oled kindel, et soovid osa ${this._part.partNumber} hindamise lõpetada ja sellele osale pakkumuse esitanud pakkujaid tulemustest teavitada?`
            })
            const didConfirm = await new Promise<boolean>(resolve => {
                const shower = CBDialogViewShower.showQuestionDialog(questionText, () => resolve(NO))
                shower.yesButtonWasPressed = () => {
                    resolve(YES)
                    shower.dialogView.dismiss()
                }
            })
            if (didConfirm) {
                await this._executePartFinalization(expectedWinningPartOfferId)
            }
        }
        finally {
            this._isFinalizingEvaluation = NO
            this.finalizeButton.enabled = YES
            this.finalizeButton.titleLabel.text = this._partSpecificFinalizeButtonTitle
            this.partOffersDataView.data = [...this.partOffersDataView.data]
        }
    }
    
    get _partSpecificFinalizeButtonTitle(): string {
        const showsPartIdentity = RHProcurementPartDisplay.shouldShowPartIdentityForPartCount(
            this._partCount ?? 1
        )
        if (showsPartIdentity) {
            return `Lõpeta osa ${this._part.partNumber} hindamine ja teavita pakkujaid`
        }
        return RHOffersEvaluationViewTokens.finalizeButtonTitle
    }
    
    async _executePartFinalization(expectedWinningPartOfferId: string | null) {
        if (!this._procurement?._id) {
            return
        }
        
        try {
            this.finalizeButton.enabled = NO
            this.finalizeButton.titleLabel.text = "Töötlemine…"
            
            const response = await RHCore.sharedInstance.offerStore.finalizePartEvaluationAndNotify(
                this._procurement._id + "",
                this._part._id.toString(),
                expectedWinningPartOfferId
            )
            
            if (response.success) {
                // The shared object keeps every part section's finalized state current without rebuilding the view.
                this._procurement.finalizedPartIds = response.finalizedPartIds
                this._procurement.status = response.newProcurementStatus
                this._evaluationDidChange()
                this._evaluationDidFinalize()
                this.setNeedsLayoutUpToRootView()
            }
            else {
                throw new Error("Failed to finalize part evaluation")
            }
        }
        catch (error) {
            console.error("Error finalizing part evaluation:", error)
            let errorMessage = "Hindamise lõpetamine ebaõnnestus. Kontrolli ühendust ja proovi uuesti."
            if (error instanceof Error) {
                errorMessage = error.message
            }
            CBDialogViewShower.alert(errorMessage)
        }
    }


    _requestReconciliationProcurement() {
        const procurementId = this._procurement?._id?.toString()
        if (!procurementId) {
            return
        }
        this._createReconciliationDidRequest?.(procurementId, this._part._id.toString())
    }


    _requestDecisionChange() {
        if (!this._canReopenPartEvaluation || !this._procurement?._id) {
            return
        }
        const procurementId = this._procurement._id.toString()
        const dialog = new PartDecisionChangeDialogView()
        if (RHProcurementPartDisplay.shouldShowPartIdentityForPartCount(this._partCount)) {
            dialog.titleLabel.text = `Muuda osa ${this._part.partNumber} otsust`
        }
        dialog.userDidConfirmDecisionChange = async (reason, winningOfferWasWithdrawn, confirmsNoContract) => {
            this._procurement = await RHCore.sharedInstance.procurementStore.reopenPartEvaluationForProcurement(
                procurementId, this._part._id.toString(), reason, winningOfferWasWithdrawn, confirmsNoContract
            )
            this._evaluationDidChange()
            this._evaluationDidFinalize()
        }
        dialog.showInRootView(YES)
    }

    _requestPartClosureWithoutContract() {
        const procurementId = this._procurement?._id?.toString()
        if (!procurementId) {
            return
        }
        this._cancelPartDidRequest?.(procurementId, this._part._id.toString())
    }


    _confirmAbandonReconciliation() {
        if (!this._procurement?._id) {
            return
        }

        const showsPartIdentity = RHProcurementPartDisplay.shouldShowPartIdentityForPartCount(
            this.numberOfRootProcurementParts ?? this._partCount ?? 1
        )
        const questionText = IF(showsPartIdentity)(() =>
            `Kas oled kindel, et soovid osa ${this._part.partNumber} täpsustamisest loobuda? Osa jääb lepinguta ja seda ei saa hiljem muuta.`
        ).ELSE(() =>
            "Kas oled kindel, et soovid täpsustamisest loobuda? Hange jääb lepinguta ja seda ei saa hiljem muuta."
        )

        const shower = CBDialogViewShower.showQuestionDialog(questionText)
        shower.yesButtonWasPressed = () => {
            shower.dialogView.dismiss()
            void this._executeAbandonReconciliation()
        }
    }


    async _executeAbandonReconciliation() {
        if (!this._procurement?._id) {
            return
        }

        try {
            this.abandonReconciliationButton.enabled = NO
            this.abandonReconciliationButton.titleLabel.text = "Töötlemine…"

            const response = await RHCore.sharedInstance.offerStore.abandonReconciliationForPart(
                this._procurement._id + "",
                this._part._id.toString()
            )

            if (response.success) {
                this._procurement.finalizedPartIds = response.finalizedPartIds
                this._procurement.status = response.newProcurementStatus
                this._evaluationDidChange()
                this._evaluationDidFinalize()
                this.setNeedsLayoutUpToRootView()
            }
            else {
                throw new Error("Failed to abandon reconciliation")
            }
        }
        catch (error) {
            console.error("Error abandoning reconciliation for part:", error)
            this.abandonReconciliationButton.enabled = YES
            this.abandonReconciliationButton.titleLabel.text = this._partSpecificAbandonReconciliationButtonTitle
            CBDialogViewShower.alert("Täpsustamisest loobumine ebaõnnestus. Palun proovi uuesti.")
        }
    }


    get _partSpecificAbandonReconciliationButtonTitle(): string {
        const showsPartIdentity = RHProcurementPartDisplay.shouldShowPartIdentityForPartCount(
            this._partCount ?? 1
        )
        return IF(showsPartIdentity)(() =>
            `Loobu osa ${this._part.partNumber} täpsustamisest`
        ).ELSE(() =>
            "Loobu täpsustamisest"
        )
    }


    override layoutSubviews() {
        super.layoutSubviews()
        
        const padding = this.core.paddingLength
        const bounds = this.contentBoundsWithInset(padding)
        
        if (this._canReopenPartEvaluation) {
            this.changeDecisionButton.addedAsSubviewToView(this)
        }
        else {
            this.changeDecisionButton.removeFromSuperview()
        }

        if (this._isReconciliationCreationEditable) {
            this.createReconciliationButton.addedAsSubviewToView(this)
        }
        else {
            this.createReconciliationButton.removeFromSuperview()
        }

        this.closedNoWinnerInfoLabel.configureBannerWithObject({
            text: IF(this._isPartClosedWithoutContract)(() =>
                RHProcurementPartDisplay.closedWithoutContractStatusText(this.numberOfRootProcurementParts ?? this._partCount)
            ).ELSE(() => RHProcurementPartDisplay.closedEvaluationWithoutWinnerText(this._partCount))
        })
        if (this._isPartClosedWithoutContract) {
            this.finalizeButton.removeFromSuperview()
            this.finalizedInfoLabel.removeFromSuperview()
            this.closedNoWinnerInfoLabel.addedAsSubviewToView(this)
            this.reconciliationLockedInfoLabel.removeFromSuperview()
            this.abandonReconciliationButton.removeFromSuperview()
        }
        else if (this.hasNextRound || (this._isPartLockedForReconciliation && !this._isPartFinalized)) {
            this.finalizeButton.removeFromSuperview()
            this.finalizedInfoLabel.removeFromSuperview()
            this.closedNoWinnerInfoLabel.removeFromSuperview()
            this.reconciliationLockedInfoLabel.addedAsSubviewToView(this)
            if (!this._isPartFinalized && this._canFinalizePartEvaluation) {
                this.abandonReconciliationButton.addedAsSubviewToView(this)
            }
            else {
                this.abandonReconciliationButton.removeFromSuperview()
            }

        }
        else if (this._isPartFinalizationEditable) {
            this.finalizeButton.addedAsSubviewToView(this)
            this.finalizedInfoLabel.removeFromSuperview()
            this.closedNoWinnerInfoLabel.removeFromSuperview()
            this.reconciliationLockedInfoLabel.removeFromSuperview()
            this.abandonReconciliationButton.removeFromSuperview()
        }
        else if (this._isPartFinalized && this._partHasWinner) {
            this.finalizeButton.removeFromSuperview()
            this.finalizedInfoLabel.addedAsSubviewToView(this)
            this.closedNoWinnerInfoLabel.removeFromSuperview()
            this.reconciliationLockedInfoLabel.removeFromSuperview()
            this.abandonReconciliationButton.removeFromSuperview()
        }
        else if (this._isPartFinalized) {
            this.finalizeButton.removeFromSuperview()
            this.finalizedInfoLabel.removeFromSuperview()
            this.closedNoWinnerInfoLabel.addedAsSubviewToView(this)
            this.reconciliationLockedInfoLabel.removeFromSuperview()
            this.abandonReconciliationButton.removeFromSuperview()
        }
        else {
            // Not yet in the post-deadline evaluation window (e.g. the whole
            // procurement was cancelled before reaching evaluation) — neither
            // a finalize action nor a finalized banner applies here.
            this.finalizeButton.removeFromSuperview()
            this.finalizedInfoLabel.removeFromSuperview()
            this.closedNoWinnerInfoLabel.removeFromSuperview()
            this.reconciliationLockedInfoLabel.removeFromSuperview()
            this.abandonReconciliationButton.removeFromSuperview()
        }

        if (this._isPartClosureWithoutContractEditable) {
            this.closePartWithoutContractButton.addedAsSubviewToView(this)
        }
        else {
            this.closePartWithoutContractButton.removeFromSuperview()
        }

        if (this.additionalActionButton?.superview !== this) {
            this.additionalActionButton?.removeFromSuperview()
            this.additionalActionButton?.addedAsSubviewToView(this)
        }
        const actionButtons = [
            this.createReconciliationButton,
            this.finalizeButton,
            this.abandonReconciliationButton,
            this.closePartWithoutContractButton,
            this.additionalActionButton
        ].filter((button): button is RHButton => !!button && button.superview === this)
        const actionButtonWidths = actionButtons.map(button =>
            button.titleLabel.intrinsicContentWidth(RHButtons.compactHeight) +
            RHButtons.compactContentPadding * 2 +
            RHSpacing.small
        )
        const actionButtonsRowWidth = IF(actionButtons.length > 0)(() =>
            actionButtonWidths.summedValue + RHSpacing.medium * (actionButtons.length - 1)
        ).ELSE(() => 0)
        const stacksActionButtons = actionButtonsRowWidth > bounds.width
        const actionButtonHeights = actionButtons.map(button => {
            button.titleLabel.isSingleLine = !stacksActionButtons
            return Math.max(RHButtons.compactHeight,
                button.titleLabel.intrinsicContentHeight(bounds.width - button.contentPadding * 2) + button.contentPadding * 2)
        })

        bounds
            .rectangleWithHeight(this.partView)
            .assignedAsFrameOfView(this.partView)
            .IF(this.emptyLabel.hidden === NO)
            .rectangleForNextRow(padding * 0.5, this.emptyLabel)
            .assignedAsFrameOfView(this.emptyLabel)
            .ELSE_IF(this.partOffersDataView.superview === this)
            .rectangleForNextRow(padding * 0.5, this.partOffersDataView)
            .assignedAsFrameOfView(this.partOffersDataView)
            .ENDIF()
            .IF(this.changeDecisionButton.superview === this)
            .rectangleForNextRow(padding, RHButtons.compactHeight)
            .rectangleWithWidth(this.changeDecisionButton.titleLabel.intrinsicContentWidth(RHButtons.compactHeight) +
                RHButtons.compactContentPadding * 2 + RHSpacing.small, 0.5)
            .assignedAsFrameOfView(this.changeDecisionButton)
            .rectangleWithX(bounds.x).rectangleWithWidth(bounds.width)
            .ENDIF()
            .IF(this.finalizedInfoLabel.superview === this)
            .rectangleForNextRow(padding, this.finalizedInfoLabel)
            .assignedAsFrameOfView(this.finalizedInfoLabel)
            .ELSE_IF(this.closedNoWinnerInfoLabel.superview === this)
            .rectangleForNextRow(padding, this.closedNoWinnerInfoLabel)
            .assignedAsFrameOfView(this.closedNoWinnerInfoLabel)
            .ELSE_IF(this.reconciliationLockedInfoLabel.superview === this)
            .rectangleForNextRow(padding, this.reconciliationLockedInfoLabel)
            .assignedAsFrameOfView(this.reconciliationLockedInfoLabel)
            .ENDIF()
            .IF(actionButtons.length > 0)
            .rectangleForNextRow(padding, 0)
            .IF(stacksActionButtons)
            .TRANSFORM(frame => frame.framesByDistributingViewsAsColumn(actionButtons, RHSpacing.medium, actionButtonHeights).boundingBox())
            .ELSE()
            .rectangleWithHeight(RHButtons.compactHeight)
            .rectangleWithWidth(actionButtonsRowWidth, 0.5)
            .distributeViewsAlongWidth(actionButtons, nil, RHSpacing.medium, actionButtonWidths)
            .ENDIF()
            .ENDIF()
    }


    static _isLowestCompliantOffer(
        candidate: Pick<RHProcurementPartOffer, "meetsRequirements" | "price">,
        offers: readonly Pick<RHProcurementPartOffer, "meetsRequirements" | "price">[]
    ): boolean {
        const candidateAmount = candidate.price?.amount
        if (
            candidate.meetsRequirements !== YES ||
            typeof candidateAmount !== "number" ||
            !Number.isFinite(candidateAmount)
        ) {
            return NO
        }

        let lowestCompliantAmount = Number.POSITIVE_INFINITY
        for (const offer of offers) {
            const amount = offer.price?.amount
            if (
                offer.meetsRequirements === YES &&
                typeof amount === "number" &&
                Number.isFinite(amount)
            ) {
                lowestCompliantAmount = Math.min(lowestCompliantAmount, amount)
            }
        }

        return candidateAmount === lowestCompliantAmount
    }

}


// ─────────────────────────────────────────────────────────────────────────────
// "Vastab hanketingimustele" Jah / Ei control
//
// Two genuinely separate buttons, side by side with a visible gap, each with its
// own pill border. Clicking "Jah" sets YES and clicking "Ei" sets NO — there is no
// blind flip — so the procurer cannot accidentally invert an already-correct verdict
// by clicking the wrong button. The selected button uses the shared success-green /
// soft-danger colour language; the unselected one is a quiet outlined pill.
// ─────────────────────────────────────────────────────────────────────────────

class MeetsRequirementsToggleView extends UIView {
    
    readonly yesButtonLabel = new UITextView(this.elementID + "YesButton")
    readonly noButtonLabel = new UITextView(this.elementID + "NoButton")
    
    /** Called with YES when "Jah" is pressed, NO when "Ei" is pressed. */
    valueDidChange?: (meetsRequirements: boolean) => void
    
    _value?: boolean
    _editable = YES
    
    constructor(elementID?: string) {
        super(elementID);
        
        (this as MeetsRequirementsToggleView).configureWithObject({
            backgroundColor: UIColor.transparentColor
        })
        
        this._configureButton(this.yesButtonLabel, "Jah")
        this._configureButton(this.noButtonLabel, "Ei")
        this.yesButtonLabel.addedAsSubviewToView(this)
        this.noButtonLabel.addedAsSubviewToView(this)
        
        this.yesButtonLabel.controlEventTargetAccumulator.PointerUpInside = () => {
            this._handleButtonPress(YES)
        }
        this.noButtonLabel.controlEventTargetAccumulator.PointerUpInside = () => {
            this._handleButtonPress(NO)
        }
        
        this._applyButtonAppearance()
    }
    
    _configureButton(buttonLabel: UITextView, text: string) {
        buttonLabel.configureWithObject({
            text,
            textAlignment: UITextView.textAlignment.center,
            userInteractionEnabled: YES,
            nativeSelectionEnabled: NO,
            style: {
                boxSizing: "border-box",
                borderStyle: "solid",
                borderWidth: "1px",
                borderRadius: "999px",
                fontSize: RHTables.rowFontSize,
                fontWeight: "600",
                cursor: "pointer",
                lineHeight: `${RHOffersEvaluationViewTokens.partOffersMeetsRequirementsTogglePillSegmentHeight}px`
            }
        })
    }
    
    _handleButtonPress(meetsRequirements: boolean) {
        if (!this._editable) {
            return
        }
        this.valueDidChange?.(meetsRequirements)
    }
    
    get value(): boolean | undefined {
        return this._value
    }
    
    set value(value: boolean | undefined) {
        this._value = value
        this._applyButtonAppearance()
    }
    
    get editable(): boolean {
        return this._editable
    }
    
    set editable(editable: boolean) {
        this._editable = editable
        const cursorStyle = IF(editable)(() => "pointer").ELSE(() => "default")
        this.yesButtonLabel.configureWithObject({ style: { cursor: cursorStyle } })
        this.noButtonLabel.configureWithObject({ style: { cursor: cursorStyle } })
    }
    
    _applyButtonAppearance() {
        // Selected "Jah" → green fill + white text + green border.
        // Selected "Ei"  → soft-danger fill + danger text + soft-danger border.
        // Either button when not selected → quiet outlined pill with neutral text.
        const yesSelected = this._value === YES
        this.yesButtonLabel.configureWithObject({
            backgroundColor: IF(yesSelected)(() =>
                RHButtons.success.backgroundColor
            ).ELSE(() =>
                RHColors.surfaceCool
            ),
            textColor: IF(yesSelected)(() =>
                RHColors.white
            ).ELSE(() =>
                RHTables.rowTextColor
            ),
            style: {
                borderColor: IF(yesSelected)(() =>
                    RHButtons.success.backgroundColor.stringValue
                ).ELSE(() =>
                    RHColors.slateLightest.stringValue
                )
            }
        })
        
        const noSelected = this._value === NO
        this.noButtonLabel.configureWithObject({
            backgroundColor: IF(noSelected)(() =>
                RHButtons.softDanger.backgroundColor
            ).ELSE(() =>
                RHColors.surfaceCool
            ),
            textColor: IF(noSelected)(() =>
                RHButtons.softDanger.textColor
            ).ELSE(() =>
                RHTables.rowTextColor
            ),
            style: {
                borderColor: IF(noSelected)(() =>
                    RHButtons.softDanger.backgroundColor.stringValue
                ).ELSE(() =>
                    RHColors.slateLightest.stringValue
                )
            }
        })
    }
    
    override layoutSubviews() {
        super.layoutSubviews()
        
        const segmentWidth = 46
        const segmentHeight = RHOffersEvaluationViewTokens.partOffersMeetsRequirementsTogglePillSegmentHeight
        const gap = RHSpacing.small
        
        this.contentBoundsWithInset(0)
            .rectangleWithWidth(segmentWidth * 2 + gap, 0.5)
            .rectangleWithHeight(segmentHeight, 0.5)
            .distributeViewsAlongWidth(
                [this.yesButtonLabel, this.noButtonLabel],
                undefined,
                gap,
                [segmentWidth, segmentWidth]
            )
    }
    
    override intrinsicContentHeight(_constrainingWidth = 0): number {
        return RHOffersEvaluationViewTokens.partOffersMeetsRequirementsTogglePillSegmentHeight
    }
    
}
